import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import dataService from '../../services/dataService';
import '../../features/brand/cwm-tokens.css';

/**
 * Rewards panel for the dashboard tab — replaces the old standalone
 * LoyaltyScreen when embedded under the dashboard. Uses the signed-in user's
 * real name, drops the duplicate internal nav bar, fits the embedded panel
 * layout. Standalone /unified/loyalty still uses this same component (just
 * without the dashboard frame).
 */

const EASE = [0.22, 1, 0.36, 1];

// No loyalty backend exists yet (dataService.getRewardsOverview returns
// null), so every diner starts at 0 points with no history. The catalog
// stays hidden until the ledger ships — nothing is redeemable yet.

export default function UnifiedRewards({ userData = null, embedded = true }) {
  const userId = userData?.id || Number(localStorage.getItem('userId')) || null;
  // Real loyalty state once the backend ships; null means "not live yet".
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const live = await dataService.getRewardsOverview(userId);
        if (cancelled || !live) return;
        setOverview({
          points: live.points ?? 0,
          tier: live.tier || null,
          history: (live.history || []).map((h) => ({ ...h, pts: h.points ?? h.pts })),
        });
      } catch {
        // Stay on the honest "coming soon" state.
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const live = !!overview;
  const points = overview?.points ?? 0;
  const tier = overview?.tier || null;
  const history = overview?.history || [];
  const firstName = (userData?.name || '').split(' ')[0] || 'friend';

  return (
    <div style={{ padding: embedded ? '18px 20px' : '28px 20px 80px', maxWidth: 1080, margin: '0 auto' }}>

      {/* Intro */}
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.46, ease: EASE }}
        style={{ marginBottom: 14 }}
      >
        <div className="cwm-eyebrow">WELCOME BACK, {firstName.toUpperCase()}</div>
      </motion.div>

      {/* Top row: points hero + status */}
      <div className="cwm-rewards-top" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 14, marginBottom: 22 }}>
        <PointsHero points={points} tier={tier} live={live} />
        <ComingSoonCard live={live} />
      </div>

      {/* Activity */}
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.46, delay: 0.2, ease: EASE }}
        className="cwm-sheet"
        style={{ padding: 0, overflow: 'hidden' }}
      >
        <header style={{ padding: '16px 20px', borderBottom: '1px solid var(--cwm-gray-100)' }}>
          <div className="cwm-eyebrow">RECENT ACTIVITY</div>
          <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)', marginTop: 2 }}>
            Earned & redeemed
          </div>
        </header>
        {history.length === 0 ? (
          <div style={{ padding: '28px 20px', textAlign: 'center', fontSize: 13, fontWeight: 700, color: 'var(--cwm-gray-500)' }}>
            No activity yet.
          </div>
        ) : (
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {history.map((h, i) => (
              <li key={h.id ?? i} style={{
                padding: '12px 20px',
                borderBottom: i < history.length - 1 ? '1px solid var(--cwm-gray-100)' : 'none',
                display: 'flex', alignItems: 'center', gap: 14,
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 12,
                  display: 'grid', placeItems: 'center', flexShrink: 0,
                  background: h.kind === 'redeem' ? 'var(--cwm-red-light)' : 'var(--cwm-blue-pale)',
                  color: h.kind === 'redeem' ? 'var(--cwm-red-text)' : 'var(--cwm-blue-deep)',
                }}>
                  <HistoryIcon name={h.icon} size={16} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--cwm-ink)' }}>{h.title}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--cwm-gray-500)', marginTop: 2 }}>{h.sub}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{
                    fontSize: 14, fontWeight: 900,
                    color: h.kind === 'redeem' ? 'var(--cwm-red-text)' : 'var(--cwm-green-text)',
                  }}>
                    {h.pts > 0 ? '+' : ''}{h.pts} pts
                  </div>
                  {h.amt && (
                    <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--cwm-gray-500)' }}>{h.amt}</div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </motion.div>

      <style>{`
        @media (min-width: 720px) {
          .cwm-rewards-top { grid-template-columns: 1.15fr 0.85fr !important; }
        }
      `}</style>
    </div>
  );
}

// ---------- sub-components ----------

function PointsHero({ points, tier, live }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -18 }} animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.46, delay: 0.08, ease: EASE }}
      style={{
        position: 'relative', overflow: 'hidden',
        borderRadius: 24, padding: 24,
        background: 'linear-gradient(160deg, #1E9BFF 0%, #0F7FE8 60%, #023E8A 100%)',
        color: '#fff',
        boxShadow: '0 24px 48px rgba(15,127,232,0.32), inset 0 1px 0 rgba(255,255,255,0.2)',
      }}
    >
      <div aria-hidden style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: 'radial-gradient(circle, rgba(255,255,255,0.22) 0, rgba(255,255,255,0.22) 1.5px, transparent 1.6px)',
        backgroundSize: '22px 22px', opacity: 0.28,
      }}/>
      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ fontSize: 10.5, fontWeight: 900, letterSpacing: '0.12em', textTransform: 'uppercase', opacity: 0.78 }}>
            POINTS BALANCE
          </div>
          <span style={{
            padding: '4px 10px', borderRadius: 999,
            background: 'rgba(255,255,255,0.22)', color: '#fff',
            fontSize: 11, fontWeight: 900, letterSpacing: '0.08em',
            backdropFilter: 'blur(4px)',
          }}>
            {live && tier ? `CWM ${String(tier).toUpperCase()}` : 'COMING SOON'}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, lineHeight: 1 }}>
          <div style={{ fontSize: 56, fontWeight: 900, letterSpacing: '-0.03em' }}>
            {points.toLocaleString()}
          </div>
          <div style={{ fontSize: 18, fontWeight: 900, opacity: 0.8 }}>pts</div>
        </div>
        <div style={{ fontSize: 13.5, fontWeight: 600, opacity: 0.94 }}>
          {live ? 'Your ChatWithMenu rewards balance.' : 'Rewards are coming soon — points will start counting once they launch.'}
        </div>
      </div>
    </motion.div>
  );
}

function ComingSoonCard({ live }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 18 }} animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.46, delay: 0.12, ease: EASE }}
      className="cwm-sheet"
      style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      <div className="cwm-eyebrow" style={{ color: 'var(--cwm-blue-deep)' }}>{live ? 'REWARDS' : 'COMING SOON'}</div>
      <div style={{ fontSize: 17, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '-0.01em' }}>
        Earn points for dining out safely
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--cwm-gray-700)', fontWeight: 600, lineHeight: 1.5 }}>
        {live
          ? 'Redemptions open as rewards roll out.'
          : "We're building a rewards program for ChatWithMenu diners. Nothing to redeem yet — we'll let you know when it's live."}
      </div>
    </motion.div>
  );
}

// ---------- icons ----------
function HistoryIcon({ name, size = 16 }) {
  const paths = {
    restaurant: 'M7 2v6a3 3 0 006 0V2M17 2v9M17 11c0 3 2 6 2 6h-4s2-3 2-6M10 11v11M7 22h6',
    gift:       'M20 12v9H4v-9M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 110-5c3 0 4.5 5 4.5 5z',
    shield:     'M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z',
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d={paths[name] || paths.restaurant}/></svg>;
}
